/* eslint-disable max-lines */
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import type Konva from "konva";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import { Circle, Group, Layer, Text } from "react-konva";
import { useLocation, useNavigate } from "react-router";

import {
  mobileSizePlacementOpenAtom,
  refineArtworkOpenAtom,
  useArtworkForm,
  usePlacementPosition,
  type InteractionMode
} from "@design-lab/entities/artwork";
import { calculateScaleFromSize } from "@design-lab/entities/artwork/lib/artworkScale";
import { calculatedArtworkDimensions } from "@design-lab/entities/artwork/lib/calculateDimensions";
import { reconcileResize } from "@design-lab/entities/artwork/lib/reconcileDimensions";
import { useCurrentComposition } from "@design-lab/entities/composition";
import {
  activePinIdAtom,
  currentPinningSessionAtom,
  hoveredPinIdAtom,
  hoveredPinSourceAtom,
  MAX_PINS,
  PIN_COLORS,
  pinningMobileViewAtom,
  pinningModeAtom,
  pinsAtom,
  pinningImageApprovalAtom,
  showPinsAtom,
  type Pin
} from "@design-lab/entities/pinning";
import { COLORS, type BackgroundType } from "@design-lab/shared/constants";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { handleArtworkSelect, trackDesignLabPinCreated } from "@design-lab/shared/lib/analytics";
import { getSideFromPlacement } from "@design-lab/shared/lib/artworkUtils";
import { designSubTabAtom } from "@design-lab/shared/lib/tab-navigation";
import type { PlacementFormPosition } from "@design-lab/shared/types";
import { useToast } from "@design-lab/shared/ui";
import { generateUUID } from "common/utils/uuid";
import { usePlacementZoomAndPan } from "../hooks/usePlacementZoomAndPan";
import { useSpaceKey } from "../hooks/useSpaceKey";
import { gestureStateRef, isTwoFingerGestureAtom } from "../model/state";
import type { BoundingBox, ShirtData, Surface } from "../model/types";
import { useCanvasVisibility } from "../model/useCanvasVisibility";
import { AreasOfInterestOverlay } from "./AreasOfInterestOverlay";
import { ArtworkGroup } from "./ArtworkGroup";
import { Neckline } from "./Neckline";
import { PrintableArea } from "./PrintableArea";
import { RefineArtworkGroup } from "./RefineArtworkGroup";
import { RefineResponsiveStage } from "./RefineResponsiveStage";
import { ResponsiveStage } from "./ResponsiveStage";
import { ShirtSurface } from "./ShirtSurface";

export type CanvasProps = {
  surface: Surface | null | undefined;
  /**
   * Reference bounding box used for centering the zoom.
   * For consistent UX we always use the front printable area
   * so that switching placements does not move the viewport.
   */
  referenceBoundingBox?: BoundingBox;
  placement?: PlacementFormPosition;
  artworkImage?: HTMLImageElement | undefined;
  readonly?: boolean;
  onArtworkRemove?: () => void;
  /**
   * The surface definition for the front placement. Needed for certain
   * placements (e.g. frontLeftChest) where we have to position guides based on
   * the front neckline rather than the current placement.
   */
  frontSurface?: Surface;
  /**
   * All available surfaces for showing multiple printable areas during drag
   */
  allSurfaces?: { placement: PlacementFormPosition; surface: Surface }[];
  backgroundType: BackgroundType;
  activeTool?: string;
  onShirtLoad?: (shirtData: ShirtData) => void;
  interactionMode?: InteractionMode;
  isSpaceHeld?: boolean;
  disablePanning?: boolean;
  onArtworkLayoutChange?: (
    layout: { x: number; y: number; width: number; height: number } | null
  ) => void;
  onImageClick?: () => void;
};

/* eslint-disable max-lines-per-function */
export function Canvas({
  surface,
  referenceBoundingBox,
  placement,
  readonly = false,
  artworkImage,
  onArtworkRemove,
  frontSurface,
  backgroundType,
  activeTool,
  onShirtLoad,
  allSurfaces,
  interactionMode = "cursor",
  isSpaceHeld = false,
  disablePanning = false,
  onArtworkLayoutChange,
  onImageClick
}: CanvasProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDesktop } = useResponsive();

  const { zoom } = usePlacementZoomAndPan();

  const isRefineMode = useAtomValue(refineArtworkOpenAtom);
  const isPinningMode = useAtomValue(pinningModeAtom);
  // MN-7773: `activeSubTab` is the synchronous source of truth for the user's
  // current side. The `placement` prop is derived from a form lens that lags
  // by one render commit after a sub-tab switch (see state.tsx post-commit
  // sync effect). `handleResize` uses this to compute the correct write target.
  const activeSubTab = useAtomValue(designSubTabAtom);
  const currentPinningSession = useAtomValue(currentPinningSessionAtom);
  const setIsSizePlacementOpen = useSetAtom(mobileSizePlacementOpenAtom);

  // Set up space key listener and get state
  const isSpaceHeldFromHook = useSpaceKey();

  const isTwoFingerGesture = useAtomValue(isTwoFingerGestureAtom);
  const [isArtworkSelected, setIsArtworkSelected] = useState(true);
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0
  });

  const [shirtData, setShirtData] = useState<ShirtData | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isHoveredPrintableArea, setIsHoveredPrintableArea] = useState(false);
  const [hoveredPrintableArea, setHoveredPrintableArea] = useState<Surface | null>(null);
  const hoveredPrintableAreaRef = useRef<Surface | null>(null);

  const [pins, setPins] = useAtom(pinsAtom);
  const [activePinId, setActivePinId] = useAtom(activePinIdAtom);
  const [hoveredPinId, setHoveredPinId] = useAtom(hoveredPinIdAtom);
  const [hoveredPinSource, setHoveredPinSource] = useAtom(hoveredPinSourceAtom);
  const setPinningMobileView = useSetAtom(pinningMobileViewAtom);
  const showPins = useAtomValue(showPinsAtom);
  const imageApproval = useAtomValue(pinningImageApprovalAtom);

  const { error: showErrorToast } = useToast();

  const { form, lens } = useArtworkForm();
  const { currentProduct } = useCurrentComposition();

  const currentPlacementLens = lens.focus("currentPlacement");
  const selectedFrontPlacementLens = lens.focus("selectedFrontPlacement");
  const currentPlacement = usePlacementPosition(currentPlacementLens);
  const selectedFrontPlacement = usePlacementPosition(selectedFrontPlacementLens);

  const artworkId = placement ? form.watch(`${placement}.artworkId`) : undefined;

  const side = placement === "back" ? "back" : "front";

  const isDraggingEnabled = useMemo(() => {
    if (placement === "back") {
      return false;
    }

    const { sides } = currentProduct?.surfaceMeta || {};
    const frontOptions = sides?.front || [];

    return frontOptions.length > 1;
  }, [placement, currentProduct]);

  const [sizeLabelTagFill, sizeLabelTextFill] = surface?.isDark
    ? [COLORS.WHITE_BACKGROUND, COLORS.BLACK_TEXT]
    : [COLORS.BLACK_TEXT, COLORS.WHITE_BACKGROUND];

  // Watch all relevant placement sizes to avoid stale values when the `name`
  // string changes during placement switches. Then select the active one.
  const frontSize = useWatch({
    control: form.control,
    name: "front.size",
    defaultValue: { width: 0, height: 0 }
  });
  const backSize = useWatch({
    control: form.control,
    name: "back.size",
    defaultValue: { width: 0, height: 0 }
  });
  const frontLeftChestSize = useWatch({
    control: form.control,
    name: "frontLeftChest.size",
    defaultValue: { width: 0, height: 0 }
  });

  const artworkSize = useMemo(() => {
    if (!placement) return { width: 0, height: 0 };
    if (placement === "front") return frontSize as { width: number; height: number };
    if (placement === "back") return backSize as { width: number; height: number };
    if (placement === "frontLeftChest")
      return frontLeftChestSize as { width: number; height: number };
    return { width: 0, height: 0 };
  }, [placement, frontSize, backSize, frontLeftChestSize]);

  const finalArtworkSize = placement ? artworkSize : undefined;
  // If readonly (e.g. pinning mode), force selection off
  const isSelected = isArtworkSelected && !readonly;

  const printableArea = useMemo(() => {
    if (!shirtData || !surface) return null;

    const { boundingBox } = surface;
    const { scale, offsetX, offsetY } = shirtData;

    return {
      left: boundingBox.left * scale + offsetX,
      top: boundingBox.top * scale + offsetY,
      width: boundingBox.width * scale,
      height: boundingBox.height * scale
    };
  }, [surface, shirtData]);

  const artworkDimensions = useMemo(() => {
    if (
      !artworkImage ||
      !finalArtworkSize ||
      typeof finalArtworkSize !== "object" ||
      !("width" in finalArtworkSize) ||
      !("height" in finalArtworkSize) ||
      !printableArea ||
      !shirtData ||
      !shirtData.height ||
      !shirtData.width ||
      !surface ||
      !placement
    )
      return undefined;

    const { ppi } = surface;
    const { scale } = shirtData;

    const widthInPixels = finalArtworkSize.width * ppi * scale;
    const heightInPixels = finalArtworkSize.height * ppi * scale;
    const x = printableArea.left + (printableArea.width - widthInPixels) / 2;
    const y = printableArea.top;

    return { x, y, width: widthInPixels, height: heightInPixels };
  }, [artworkImage, printableArea, surface, shirtData, finalArtworkSize, placement]);

  useEffect(() => {
    onArtworkLayoutChange?.(artworkDimensions || null);
  }, [artworkDimensions, onArtworkLayoutChange]);

  const handleShirtLoad = useCallback(
    (data: ShirtData) => {
      setShirtData(data);
      if (onShirtLoad) {
        onShirtLoad(data);
      }
    },
    [onShirtLoad]
  );

  const handleSelect = useCallback(() => {
    if (readonly) return;

    if (artworkId) {
      handleArtworkSelect(side, artworkId);
    }

    setIsArtworkSelected(true);

    if (isDesktop) {
      navigate(`${location.pathname}${location.search}`);
    } else {
      setIsSizePlacementOpen(true);
    }
  }, [
    readonly,
    side,
    artworkId,
    isDesktop,
    navigate,
    location.pathname,
    location.search,
    setIsSizePlacementOpen
  ]);

  const handleDeselect = useCallback(() => {
    setIsArtworkSelected(false);
  }, [setIsArtworkSelected]);

  const handleResize = useCallback(
    (widthInches: number, heightInches: number) => {
      if (!placement) return;

      // MN-7773: Re-derive the write target at event time. `placement` is
      // sourced from `currentPlacement`, which is updated post-commit by the
      // sub-tab sync effect in `shared/ui/layouts/state.tsx` — so it lags
      // `designSubTabAtom` by one render. Writing through the stale prop
      // would put the new side's dimensions in the old side's `.size` field
      // (the residual Class A bug PR #57465 fixed for DesignArtwork.tsx).
      // Use `activeSubTab` plus `selectedFrontPlacement` for the synchronous
      // side answer.
      // Refine mode does not drive sub-tabs — its parent passes the placement
      // prop directly, so it is authoritative there.
      let targetPlacement: PlacementFormPosition;
      if (isRefineMode) {
        targetPlacement = placement;
      } else if (activeSubTab === "back") {
        targetPlacement = "back";
      } else if (selectedFrontPlacement.placement === "frontLeftChest") {
        targetPlacement = "frontLeftChest";
      } else {
        targetPlacement = "front";
      }

      // PRINT-6608 (PIP-3131 Class D): the drag-resize handle reports raw
      // (widthInches, heightInches) from the transform. Konva is configured
      // for aspect-locked drag in normal use, but axis-locked side handles
      // and surface-bound clamping can still produce an off-aspect pair
      // (e.g. surface-max 11.1 × 14 against a 4:1 logo). Snap to the closest
      // aspect-preserving pair using the artwork's natural aspect from
      // bounds.maxAspectRatioByWidth — same source-of-truth as the
      // useDesignLabArtworkAutoSizing reconciler.
      //
      // Codex P2: compute bounds from `targetPlacement`'s own surface, not
      // the lagging `surface` prop. During sub-tab transitions, `surface`
      // is still the previous side's printable area while `targetPlacement`
      // already names the new side; reconciling against the previous side's
      // bounds would write dims that don't fit the new side. Resolve the
      // target surface from `allSurfaces` per-write and recompute bounds.
      const targetSurface =
        allSurfaces?.find(s => s.placement === targetPlacement)?.surface ?? surface ?? undefined;
      const targetBounds = calculatedArtworkDimensions(artworkImage, targetSurface);

      const newSize = reconcileResize(widthInches, heightInches, targetBounds);

      form.setValue(`${targetPlacement}.size`, newSize, { shouldDirty: true });

      if (targetBounds && targetBounds.maxWidthInches > 0) {
        const newScale = calculateScaleFromSize(newSize, targetBounds);
        const infoPath = getSideFromPlacement(targetPlacement);
        form.setValue(`info.${infoPath}.scale`, newScale);
      }

      if (!isDesktop) {
        setIsSizePlacementOpen(true);
      }
    },
    // `placement` stays in the deps because it gates the early return and the
    // Refine-mode branch above; `activeSubTab` and `selectedFrontPlacement.placement`
    // are the synchronous inputs to the MN-7773 write-target derivation.
    // `allSurfaces`, `surface`, and `artworkImage` are inputs to the
    // target-bounds lookup (PRINT-6608 Codex P2).
    [
      form,
      placement,
      isRefineMode,
      activeSubTab,
      selectedFrontPlacement.placement,
      allSurfaces,
      surface,
      artworkImage,
      isDesktop,
      setIsSizePlacementOpen
    ]
  );

  // Helper function to find which printable area contains a position
  // Prioritizes larger printable areas when multiple areas overlap, but only if not fully contained in smaller area
  const findPrintableAreaAtPosition = useCallback(
    (params: {
      x: number;
      y: number;
      width: number;
      height: number;
    }): { placement: PlacementFormPosition; surface: Surface } | null => {
      const { x: initialX, y: initialY, width, height } = params;
      const x = initialX + width / 2;
      const y = initialY + height / 2;

      if (!allSurfaces || !shirtData) return null;

      const overlappingAreas: {
        item: { placement: PlacementFormPosition; surface: Surface };
        area: number;
        isFullyContained: boolean;
      }[] = [];

      for (const item of allSurfaces) {
        const { boundingBox } = item.surface;
        const { scale, offsetX, offsetY } = shirtData;

        const printableAreaLeft = boundingBox.left * scale + offsetX;
        const printableAreaTop = boundingBox.top * scale + offsetY;
        const printableAreaWidth = boundingBox.width * scale;
        const printableAreaHeight = boundingBox.height * scale;

        // Check if artwork center is within this printable area
        const centerInArea =
          x >= printableAreaLeft &&
          x <= printableAreaLeft + printableAreaWidth &&
          y >= printableAreaTop &&
          y <= printableAreaTop + printableAreaHeight;

        if (centerInArea) {
          // Calculate artwork bounds for containment check
          const artworkLeft = x - width / 2;
          const artworkTop = y - height / 2;
          const artworkRight = x + width / 2;
          const artworkBottom = y + height / 2;

          // Check if the entire artwork is fully contained within this area
          const isFullyContained =
            artworkLeft >= printableAreaLeft &&
            artworkRight <= printableAreaLeft + printableAreaWidth &&
            artworkTop >= printableAreaTop &&
            artworkBottom <= printableAreaTop + printableAreaHeight;

          // Calculate area size to prioritize larger areas
          const area = printableAreaWidth * printableAreaHeight;
          overlappingAreas.push({ item, area, isFullyContained });
        }
      }

      // Return the largest overlapping area, or null if none found
      if (overlappingAreas.length === 0) return null;

      // Check if any area is contained within another area
      for (let i = 0; i < overlappingAreas.length; i += 1) {
        for (let j = 0; j < overlappingAreas.length; j += 1) {
          const areaA = overlappingAreas[i];
          const areaB = overlappingAreas[j];

          const shouldSkip = i === j || !areaA || !areaB;
          if (!shouldSkip) {
            // Check if areaA is contained within areaB
            const { boundingBox: bboxA } = areaA.item.surface;
            const { boundingBox: bboxB } = areaB.item.surface;
            const { scale, offsetX, offsetY } = shirtData;

            const leftA = bboxA.left * scale + offsetX;
            const topA = bboxA.top * scale + offsetY;
            const rightA = leftA + bboxA.width * scale;
            const bottomA = topA + bboxA.height * scale;

            const leftB = bboxB.left * scale + offsetX;
            const topB = bboxB.top * scale + offsetY;
            const rightB = leftB + bboxB.width * scale;
            const bottomB = topB + bboxB.height * scale;

            // If areaA is completely contained within areaB, prioritize areaA (the contained area)
            if (leftA >= leftB && rightA <= rightB && topA >= topB && bottomA <= bottomB) {
              return areaA.item;
            }
          }
        }
      }

      // If any area fully contains the artwork, prioritize the smallest fully containing area
      const fullyContainedAreas = overlappingAreas.filter(a => a.isFullyContained);
      if (fullyContainedAreas.length > 0) {
        fullyContainedAreas.sort((a, b) => a.area - b.area); // Smallest first for fully contained
        return fullyContainedAreas[0]?.item || null;
      }

      // Otherwise, sort by area size (largest first) for overlapping but not fully contained areas
      overlappingAreas.sort((a, b) => b.area - a.area);
      return overlappingAreas[0]?.item || null;
    },
    [allSurfaces, shirtData]
  );

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
    setHoveredPrintableArea(null);
  }, []);

  const handleDragEnd = useCallback(
    (params: { x: number; y: number; width: number; height: number }): boolean => {
      if (!allSurfaces || !shirtData) return false;

      const hoveredSurface = hoveredPrintableAreaRef.current;
      const targetArea = hoveredSurface
        ? allSurfaces.find(s => s.surface === hoveredSurface)
        : findPrintableAreaAtPosition(params);

      const placementChanged = targetArea && targetArea.placement !== placement;

      if (targetArea) {
        if (targetArea.placement.startsWith("front")) {
          selectedFrontPlacement.setPlacement(targetArea.placement);
        }

        currentPlacement.setPlacement(targetArea.placement);

        if (targetArea.placement === "frontLeftChest") {
          form.setValue(`${targetArea.placement}.size`, { width: 0, height: 0 });
        }
      }

      setIsDragging(false);
      setHoveredPrintableArea(null);
      hoveredPrintableAreaRef.current = null;

      return Boolean(placementChanged);
    },
    [
      form,
      currentPlacement,
      selectedFrontPlacement,
      allSurfaces,
      shirtData,
      placement,
      findPrintableAreaAtPosition
    ]
  );

  const handleDragMove = useCallback(
    (params: { x: number; y: number; width: number; height: number }) => {
      if (!allSurfaces || !shirtData) return;

      const printableAreaAtPosition = findPrintableAreaAtPosition(params);
      const targetSurface = printableAreaAtPosition?.surface ?? null;

      hoveredPrintableAreaRef.current = targetSurface;
      setHoveredPrintableArea(targetSurface);
    },
    [allSurfaces, shirtData, findPrintableAreaAtPosition]
  );

  const [roundedWidth, roundedHeight] = useMemo(() => {
    if (
      !finalArtworkSize ||
      typeof finalArtworkSize !== "object" ||
      !("width" in finalArtworkSize) ||
      !("height" in finalArtworkSize)
    )
      return [0, 0];

    return [
      Math.round(finalArtworkSize.width * 100) / 100,
      Math.round(finalArtworkSize.height * 100) / 100
    ];
  }, [finalArtworkSize]);

  const { showPrintableArea, showNeckline, showWidthLabel, showHeightLabel } = useCanvasVisibility({
    shirtData,
    artworkImage,
    readonly,
    roundedWidth,
    roundedHeight,
    isSelected,
    isDragging
  });

  // Hide size labels during dragging
  const shouldShowWidthLabel = showWidthLabel && !isDragging;
  const shouldShowHeightLabel = showHeightLabel && !isDragging;

  // Calculate zoom center based on the reference bounding box (always front printable area)
  const zoomCenter = useMemo(() => {
    if (artworkDimensions) {
      return {
        x: artworkDimensions.x + artworkDimensions.width / 2,
        y: artworkDimensions.y + artworkDimensions.height / 2
      };
    }

    if (!shirtData || !referenceBoundingBox) return undefined;

    const { scale, offsetX, offsetY } = shirtData;

    return {
      x: (referenceBoundingBox.left + referenceBoundingBox.width / 2) * scale + offsetX,
      y: (referenceBoundingBox.top + referenceBoundingBox.height / 2) * scale + offsetY
    };
  }, [referenceBoundingBox, shirtData, artworkDimensions]);

  const createUuid = useCallback(() => generateUUID(), []);

  const tryPlacePin = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (!isPinningMode || !artworkDimensions || !artworkImage || imageApproval) return false;

      // Block pin placement if a gesture (drag or pinch) is active or just ended
      // Use synchronous ref for immediate response
      if (
        isTwoFingerGesture ||
        gestureStateRef.isGestureActive ||
        gestureStateRef.hasMoved ||
        gestureStateRef.gestureJustEnded
      ) {
        return false;
      }

      // Check if click was on an existing pin marker - if so, don't try to place a new pin
      const clickTarget = e.target;
      const isClickOnPin =
        clickTarget.name() === "pin-marker" || clickTarget.getParent()?.name() === "pin-marker";
      if (isClickOnPin) {
        return false;
      }

      // Check if there's an active pin that's not finalized
      const activePin = pins.find(p => p.id === activePinId);
      const isActivePinUnfinished =
        activePin && activePin.state !== "ready" && activePin.state !== "unsupported";

      // A pin can be repositioned if it hasn't been synced to backend yet (draft with no messages)
      const canRepositionActivePin =
        isActivePinUnfinished && activePin.state === "draft" && activePin.messages.length === 0;

      // Block if there's an unfinished pin that can't be repositioned
      if (isActivePinUnfinished && !canRepositionActivePin) {
        return false;
      }

      // Check pin limit only when creating a new pin (not when repositioning)
      if (!canRepositionActivePin && pins.length >= MAX_PINS) {
        showErrorToast({
          title: "Pin limit reached",
          description: `You cannot add more than ${MAX_PINS} pins to the image.`
        });
        return false;
      }

      const stage = e.target.getStage();
      if (!stage) return false;

      const pointerPosition = stage.getRelativePointerPosition();
      if (!pointerPosition) return false;

      const scenePoint = { x: pointerPosition.x, y: pointerPosition.y };

      const imageLeft = artworkDimensions.x;
      const imageTop = artworkDimensions.y;
      const renderedWidth = artworkDimensions.width;
      const renderedHeight = artworkDimensions.height;

      const withinBounds =
        scenePoint.x >= imageLeft &&
        scenePoint.x <= imageLeft + renderedWidth &&
        scenePoint.y >= imageTop &&
        scenePoint.y <= imageTop + renderedHeight;

      if (!withinBounds) return false;

      // Call onImageClick callback when user clicks on the image
      onImageClick?.();

      const naturalWidth = artworkImage.naturalWidth || artworkImage.width;
      const naturalHeight = artworkImage.naturalHeight || artworkImage.height;

      const xNatural = Math.round((scenePoint.x - imageLeft) * (naturalWidth / renderedWidth));
      const yNatural = Math.round((scenePoint.y - imageTop) * (naturalHeight / renderedHeight));

      const clampedX = Math.min(Math.max(xNatural, 0), Math.max(naturalWidth - 1, 0));
      const clampedY = Math.min(Math.max(yNatural, 0), Math.max(naturalHeight - 1, 0));

      // If we can reposition the active pin, update its coordinates instead of creating a new pin
      if (canRepositionActivePin) {
        setPins(prev =>
          prev.map(pin =>
            pin.id === activePin.id ? { ...pin, coordinates: { x: clampedX, y: clampedY } } : pin
          )
        );
        return true;
      }

      const newPin: Pin = {
        id: createUuid(),
        coordinates: { x: clampedX, y: clampedY },
        state: "draft",
        messages: [],
        isNew: true
      };

      setPins(prev => [...prev, newPin]);
      setActivePinId(newPin.id);

      // Track pin creation
      if (currentPinningSession) {
        trackDesignLabPinCreated({
          artworkId: currentPinningSession.artworkId,
          sessionId: currentPinningSession.sessionId,
          pinId: newPin.id
        });
      }

      return true;
    },
    [
      artworkDimensions,
      artworkImage,
      createUuid,
      isPinningMode,
      imageApproval,
      setActivePinId,
      setPins,
      pins,
      activePinId,
      currentPinningSession,
      showErrorToast,
      isTwoFingerGesture,
      onImageClick
    ]
  );

  const handleStageClick = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (tryPlacePin(e)) return;
      const clickedOnEmpty = e.target === e.target.getStage() || e.target.hasName("shirt-image");
      if (clickedOnEmpty) {
        handleDeselect();
      }
    },
    [handleDeselect, tryPlacePin]
  );

  useEffect(() => {
    if (artworkImage) {
      setIsArtworkSelected(true);
    } else {
      setIsArtworkSelected(false);
    }
  }, [artworkImage, setIsArtworkSelected]);

  // Calculate content bounds for panning constraints
  const contentBounds = useMemo(() => {
    if (!shirtData) return undefined;

    const { scale, offsetX, offsetY, width, height } = shirtData;
    return {
      x: offsetX,
      y: offsetY,
      width: width * scale,
      height: height * scale
    };
  }, [shirtData]);

  const surfacesToRender = useMemo(
    () =>
      (allSurfaces ?? [])
        .filter(s => {
          if (isDragging) return true;
          return s.surface === surface;
        })
        .map(s => ({
          ...s,
          isCurrent: s.surface === surface,
          isHovered: hoveredPrintableArea === s.surface
        })),
    [allSurfaces, isDragging, surface, hoveredPrintableArea]
  );

  // Check if a completed pin is being hovered
  const isHoveringCompletedPin = useMemo(
    () =>
      hoveredPinId &&
      pins.some(p => p.id === hoveredPinId && (p.state === "ready" || p.state === "unsupported")),
    [hoveredPinId, pins]
  );

  const layerRef = useRef<Konva.Layer>(null);

  // Configure image smoothing on layer canvas context to prevent aliasing artifacts
  // This needs to be set on every draw since Konva resets the context
  useEffect(() => {
    if (!layerRef.current) {
      return () => {
        // No cleanup needed if layer ref is not available
      };
    }

    const layer = layerRef.current;
    const canvas = layer.getCanvas() as unknown as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
    }

    // Set up draw handler to ensure smoothing is enabled on every draw
    const originalDraw = layer.draw.bind(layer);
    layer.draw = function drawScene() {
      const drawCanvas = this.getCanvas() as unknown as HTMLCanvasElement;
      const drawCtx = drawCanvas.getContext("2d");
      if (drawCtx) {
        drawCtx.imageSmoothingEnabled = true;
        drawCtx.imageSmoothingQuality = "high";
      }
      return originalDraw();
    };

    return () => {
      // Restore original draw method on cleanup
      layer.draw = originalDraw;
    };
  }, [artworkImage, placement]);

  if (!surface) {
    return null;
  }

  // Determine which artwork group to render
  const renderArtworkGroup = () => {
    if (!placement) return null;

    if (isRefineMode) {
      return (
        <RefineArtworkGroup
          artworkId={artworkId || ""}
          placement={placement}
          artworkImage={artworkImage}
          artworkDimensions={artworkDimensions}
          printableArea={printableArea ?? undefined}
          {...(activeTool ? { activeTool } : {})}
          interactionMode={interactionMode}
          isSpaceHeld={isSpaceHeld}
        />
      );
    }

    return (
      <ArtworkGroup
        artworkId={artworkId || ""}
        placement={placement}
        artworkImage={artworkImage}
        artworkDimensions={artworkDimensions}
        readonly={readonly || isSpaceHeldFromHook || isTwoFingerGesture}
        draggable={
          isDraggingEnabled && !isSpaceHeldFromHook && !isTwoFingerGesture && !isPinningMode
        }
        isHovered={isHoveredPrintableArea}
        isSelected={isSelected}
        onSelect={handleSelect}
        onResize={handleResize}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        printableArea={printableArea ?? undefined}
        ppi={surface.ppi}
        scale={shirtData?.scale ?? 1}
        zoom={zoom}
        roundedWidth={roundedWidth}
        roundedHeight={roundedHeight}
        showWidthLabel={shouldShowWidthLabel}
        showHeightLabel={shouldShowHeightLabel}
        showRemoveButton={!isDragging}
        showResizeButton={!isDragging}
        sizeLabelTagFill={sizeLabelTagFill}
        sizeLabelTextFill={sizeLabelTextFill}
        onRemove={onArtworkRemove || undefined}
        onHover={setIsHoveredPrintableArea}
      />
    );
  };

  // Common stage props
  const commonStageProps = {
    dimensions,
    setDimensions,
    onClick: handleStageClick,
    onTap: handleStageClick,
    minZoom: 1,
    maxZoom: 10,
    disablePanning,
    ...(zoomCenter && { zoomCenter }),
    ...(contentBounds && { contentBounds })
  };

  // Shared layer content
  const layerContent = (
    <Layer ref={layerRef}>
      {/* Design Lab Rendering - surface is guaranteed to be non-null here */}
      <ShirtSurface
        surface={surface}
        canvasWidth={dimensions.width}
        canvasHeight={dimensions.height}
        onLoad={handleShirtLoad}
        backgroundType={backgroundType}
      />

      {!isRefineMode && showPrintableArea && shirtData && (
        <>
          {surfacesToRender.map((item, index) => {
            const variant = item.isHovered ? "hovered" : "drag-preview";
            return (
              <PrintableArea
                key={index}
                surface={item.surface}
                shirtData={shirtData}
                placement={surfacesToRender.length > 1 ? item.placement : undefined}
                variant={item.isCurrent ? "default" : variant}
              />
            );
          })}
        </>
      )}

      {!isRefineMode && showNeckline && placement && (
        <Neckline
          shirtData={shirtData!}
          surface={surface}
          frontSurface={frontSurface}
          isSelectedColorLight={!surface.isDark}
          placement={placement}
        />
      )}
      {renderArtworkGroup()}

      {/* Pins overlay (pinning mode only) */}
      {isPinningMode && !imageApproval && artworkDimensions && artworkImage && (
        <>
          {/* Areas of interest overlays */}
          {pins.map(pin => {
            const isCompleted = pin.state === "ready" || pin.state === "unsupported";
            const hasAreasOfInterest =
              pin.areasOfInterest && pin.areasOfInterest.objects.length > 0;

            if (!hasAreasOfInterest) return null;

            // Show areas if: toggle is ON (for non-completed) OR hovering (for completed)
            // Hide active pin's mask when hovering a completed pin to avoid showing both
            const isVisible = isCompleted
              ? hoveredPinId === pin.id
              : pin.highlightAreasEnabled !== false && !isHoveringCompletedPin;

            return (
              <AreasOfInterestOverlay
                key={`aoi-${pin.id}`}
                areasOfInterest={pin.areasOfInterest!}
                artworkDimensions={artworkDimensions}
                artworkImage={artworkImage}
                isVisible={isVisible}
                segmentationMaskUrl={pin.segmentationMaskUrl}
              />
            );
          })}

          {/* Pin markers */}
          {showPins &&
            pins
              .map((pin, index) => ({ pin, originalIndex: index }))
              .sort((a, b) => {
                // Active pin should be rendered last (on top)
                if (a.pin.id === activePinId) return 1;
                if (b.pin.id === activePinId) return -1;
                return 0;
              })
              .map(({ pin, originalIndex }) => {
                const index = originalIndex;
                const naturalWidth = artworkImage.naturalWidth || artworkImage.width;
                const naturalHeight = artworkImage.naturalHeight || artworkImage.height;

                const x =
                  artworkDimensions.x +
                  (pin.coordinates.x * artworkDimensions.width) / Math.max(naturalWidth, 1);
                const y =
                  artworkDimensions.y +
                  (pin.coordinates.y * artworkDimensions.height) / Math.max(naturalHeight, 1);

                const isActive = pin.id === activePinId;
                const isCompleted = pin.state === "ready" || pin.state === "unsupported";
                const pinColor = PIN_COLORS[index % PIN_COLORS.length] ?? "#3B82F6";

                // Hide pin when it's mask/bounding box is visible
                const hasAreasOfInterest =
                  pin.areasOfInterest && pin.areasOfInterest.objects.length > 0;
                const isHoveredFromPanel = hoveredPinId === pin.id && hoveredPinSource === "panel";

                // Active pin's mask is only visible when enabled AND not hovering a completed pin
                const isMaskVisibleForActivePin =
                  !isCompleted &&
                  hasAreasOfInterest &&
                  pin.highlightAreasEnabled !== false &&
                  !isHoveringCompletedPin;

                if (hasAreasOfInterest && (isHoveredFromPanel || isMaskVisibleForActivePin))
                  return null;

                // Check if active pin is not ready (blocks switching/deselecting)
                const currentActivePin = pins.find(p => p.id === activePinId);
                const isActivePinNotReady =
                  currentActivePin &&
                  currentActivePin.state !== "ready" &&
                  currentActivePin.state !== "unsupported";

                return (
                  <Group
                    key={pin.id}
                    name="pin-marker"
                    x={x}
                    y={y}
                    onClick={evt => {
                      evt.evt?.stopPropagation();
                      // Prevent switching/deselecting when active pin is not ready
                      if (isActivePinNotReady && pin.id !== activePinId) {
                        return;
                      }
                      setActivePinId(pin.id);
                      setPinningMobileView("detail");
                    }}
                    onTap={evt => {
                      evt.evt?.stopPropagation();
                      // Prevent switching/deselecting when active pin is not ready
                      if (isActivePinNotReady && pin.id !== activePinId) {
                        return;
                      }
                      setActivePinId(pin.id);
                      setPinningMobileView("detail");
                    }}
                    onMouseEnter={() => {
                      if (isCompleted) {
                        setHoveredPinId(pin.id);
                        setHoveredPinSource("canvas");
                      }
                    }}
                    onMouseLeave={() => {
                      if (isCompleted) {
                        setHoveredPinId(null);
                        setHoveredPinSource(null);
                      }
                    }}
                    opacity={activePinId && !isActive ? 0.4 : 1}
                  >
                    <Circle
                      radius={5}
                      fill={pinColor}
                      stroke="#ffffff"
                      strokeWidth={1.5}
                      shadowColor="rgba(0,0,0,0.25)"
                      shadowBlur={4}
                      shadowOffset={{ x: 0, y: 1 }}
                      listening={true}
                    />
                    <Text
                      text={`${index + 1}`}
                      fontSize={7}
                      fontStyle="bold"
                      fill="#ffffff"
                      width={10}
                      height={10}
                      align="center"
                      verticalAlign="middle"
                      offsetX={5}
                      offsetY={4.5}
                      listening={false}
                    />
                  </Group>
                );
              })}
        </>
      )}
    </Layer>
  );

  if (isRefineMode) {
    return (
      <RefineResponsiveStage
        {...commonStageProps}
        interactionMode={interactionMode}
        isSpaceHeld={isSpaceHeld}
      >
        {layerContent}
      </RefineResponsiveStage>
    );
  }

  return <ResponsiveStage {...commonStageProps}>{layerContent}</ResponsiveStage>;
}
